export * from './types';
export * from './Logger';
export * from './transports/LokiTransport';

import { Logger } from './Logger';
import { LOG_CONFIG } from './config';
import { LogLevel } from './types';

// Validate configuration - only log issues in development
const validateConfig = () => {
  const issues: string[] = [];
  const isDev = import.meta.env.DEV === true;

  if (!LOG_CONFIG.loki.enabled) {
    issues.push('Loki logging is disabled');
  }

  if (LOG_CONFIG.loki.enabled) {
    if (!LOG_CONFIG.loki.host) {
      issues.push('Loki host is not configured');
    }
    if (!LOG_CONFIG.loki.auth.username) {
      issues.push('Loki username is not configured');
    }
    if (!LOG_CONFIG.loki.auth.password) {
      issues.push('Loki password is not configured');
    }
  }

  if (issues.length > 0 && isDev) {
    console.warn('Logger configuration issues:', issues);
  }

  return issues;
};

// Initialize logger with configuration
const initializeLogger = () => {
  const configIssues = validateConfig();
  const isDev = import.meta.env.DEV === true;
  
  const logger = new Logger({
    minLogLevel: LOG_CONFIG.general.minLogLevel,
    additionalLabels: {
      platform: 'web',
      environment: import.meta.env.MODE || 'development',
      version: import.meta.env.VITE_APP_VERSION || '1.0.0'
    }
  });

  // Only log initialization in development
  if (isDev && LOG_CONFIG.general.enableConsole) {
    console.log('Logger initialized:', {
      enabled: LOG_CONFIG.loki.enabled,
      minLogLevel: LOG_CONFIG.general.minLogLevel,
      configIssues: configIssues.length > 0 ? configIssues : 'None'
    });
  }

  // Only send test log in development or if explicitly enabled
  if (isDev || LOG_CONFIG.loki.enabled) {
    logger.info('Logger initialized', {
      enabled: LOG_CONFIG.loki.enabled,
      environment: import.meta.env.MODE,
      version: import.meta.env.VITE_APP_VERSION
    });
  }

  return logger;
};

// Create and export the logger instance
export const logger = initializeLogger();

// Create some common loggers for different parts of the application
export const networkLogger = logger.child({ component: 'network' });
export const routerLogger = logger.child({ component: 'router' });
export const errorLogger = logger.child({ component: 'error-boundary' });

// Helper function to create component-specific loggers
export const createComponentLogger = (componentName: string) => {
  return logger.child({ component: componentName });
};

// Log network errors - critical enough to always log
export const logNetworkError = (error: any, url: string) => {
  // Don't include sensitive data in logs
  const sanitizedUrl = url.replace(/([?&](token|key|auth|password)=)[^&]+/gi, '$1[REDACTED]');
  
  const errorDetails = {
    url: sanitizedUrl,
    status: error?.response?.status,
    statusText: error?.response?.statusText,
    // Don't include potentially sensitive response data in production
    ...(import.meta.env.DEV ? { data: error?.response?.data } : {}),
    // Only include headers in development
    ...(import.meta.env.DEV ? { headers: error?.response?.headers } : {})
  };

  networkLogger.error(
    'Network Request Failed',
    new Error(error?.message || 'Network request failed'),
    errorDetails
  );
  
  // Keep console.error for local debugging only
  if (import.meta.env.DEV) {
    console.error('Network Error:', errorDetails);
  }
};

// Only log network requests in development
export const logNetworkRequest = (url: string, method: string, headers: any) => {
  // Skip logging in production to improve performance
  if (import.meta.env.PROD) return;
  
  // Don't include sensitive data in logs
  const sanitizedUrl = url.replace(/([?&](token|key|auth|password)=)[^&]+/gi, '$1[REDACTED]');
  
  // Don't log headers in production as they may contain sensitive information
  const requestDetails = {
    url: sanitizedUrl,
    method,
    ...(import.meta.env.DEV ? { headers } : {})
  };

  networkLogger.info('Network Request', requestDetails);
  
  // Keep console.log for local debugging only
  if (import.meta.env.DEV) {
    console.log('Network Request:', requestDetails);
  }
}; 
